import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type JobRow } from "@gwrctl/api-client";
import { client } from "./client.js";
import { humanizeError } from "./errors.ts";

/**
 * Bot ve sunucu panelleri verilerini "job sonucu" olarak alır — ayrı bir REST ucu
 * yok, çünkü o veriyi üreten şey zaten agent'ta çalışan iş.
 *
 * Sağlık gözcüsü bu işleri 5 dakikada bir kendiliğinden çalıştırdığından, sayfa
 * açıldığında YENİ iş yaratmak yerine en son başarılı sonucu okuyoruz: hem anında
 * dolu geliyor hem de boşuna agent meşgul edilmiyor. "Yenile" düğmesi bilinçli
 * olarak yeni bir iş başlatır.
 */
export function useLatestJobResult<T>(
  type: string,
  options: { app?: string; enabled?: boolean } = {},
): {
  data: T | null;
  job: JobRow | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  refreshing: boolean;
  /** Yenile işinin hatası (kullanıcı tetikli). */
  error: string | null;
  /** Son sonucun kendisi okunamadıysa (API hatası) — "veri yok" ile karıştırma. */
  loadError: unknown;
  reload: () => void;
} {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["latest-job", type, options.app ?? null],
    queryFn: async () => {
      const jobs = await client.jobs({ limit: 60, ...(options.app ? { app: options.app } : {}) });
      return jobs.find((j) => j.type === type && j.status === "succeeded") ?? null;
    },
    enabled: options.enabled ?? true,
    refetchInterval: 60_000,
  });

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const created = await client.createJob({
        type,
        ...(options.app ? { app: options.app } : {}),
        params: options.app ? { app: options.app } : {},
      });
      await waitForJob(created.job.id);
      await queryClient.invalidateQueries({ queryKey: ["latest-job", type, options.app ?? null] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    } catch (err) {
      setError(humanizeError(err, "Yenilenemedi"));
    } finally {
      setRefreshing(false);
    }
  }, [type, options.app, queryClient]);

  return {
    data: (query.data?.result as T | undefined) ?? null,
    job: query.data ?? null,
    isLoading: query.isLoading,
    refresh,
    refreshing,
    error,
    loadError: query.error,
    reload: () => void query.refetch(),
  };
}

const TERMINAL = new Set(["succeeded", "failed", "canceled", "expired"]);

/**
 * İş bitene kadar bekler. Canlı olay akışı varken bile basit yoklama yeterli:
 * bu yol yalnızca "yenile" gibi kullanıcı tetikli, kısa işler için kullanılıyor.
 *
 * ⚠ Başarısız / iptal / zaman aşımı FIRLATIR. Önceden işi olduğu gibi döndürüyordu;
 * çağıranlar sonucu kontrol etmediği için başarısız bir restart "oldu" görünüyordu.
 */
export async function waitForJob(jobId: string, timeoutMs = 120_000): Promise<JobRow> {
  const started = Date.now();
  for (;;) {
    const { job } = await client.job(jobId);
    if (job.status === "succeeded") return job;
    if (TERMINAL.has(job.status)) {
      throw new Error(
        job.error ??
          (job.status === "canceled"
            ? "İş iptal edildi."
            : job.status === "expired"
              ? "İş zamanında alınamadı (agent kapalı olabilir)."
              : "İş başarısız oldu."),
      );
    }
    if (Date.now() - started > timeoutMs) {
      throw new Error(
        job.status === "queued"
          ? "İş hâlâ kuyrukta — ilgili agent bağlı değil gibi görünüyor."
          : "İş beklenenden uzun sürüyor; sonucu iş kaydından takip et.",
      );
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
}

export interface RunOptions {
  type: string;
  app?: string;
  params?: unknown;
  /** Yıkıcı işlerde: kullanıcıdan alınan 6 haneli kod. */
  totp?: string;
  /** İş bitene kadar bekle ve sonucu döndür. */
  wait?: boolean;
}

/**
 * Tek seferlik iş başlatma. TOTP gereken işlerde önce onay alınır; sunucu
 * TOTP_REQUIRED derse çağıran taraf kod isteyip tekrar dener.
 */
export function useRunJob(): {
  run: (options: RunOptions) => Promise<JobRow>;
  busy: boolean;
  error: string | null;
  needsTotp: boolean;
  clearError: () => void;
} {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);

  const run = useCallback(
    async (options: RunOptions) => {
      setBusy(true);
      setError(null);
      setNeedsTotp(false);
      try {
        if (options.totp) await client.confirm(options.totp);
        const created = await client.createJob({
          type: options.type,
          ...(options.app ? { app: options.app } : {}),
          params: options.params ?? (options.app ? { app: options.app } : {}),
        });
        void queryClient.invalidateQueries({ queryKey: ["jobs"] });
        const finished = options.wait ? await waitForJob(created.job.id) : created.job;
        return finished;
      } catch (err) {
        if (err instanceof ApiError && err.needsTotp) {
          setNeedsTotp(true);
          setError("Bu işlem doğrulama kodu istiyor");
        } else {
          setError(humanizeError(err, "Başlatılamadı"));
        }
        throw err;
      } finally {
        setBusy(false);
        void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      }
    },
    [queryClient],
  );

  return { run, busy, error, needsTotp, clearError: () => setError(null) };
}
